// Personality assessment (IP-safe) — the 5 Big Five traits + 10 Metanoia archetypes.
//
// Ported verbatim from docs/PERSONALITY_ARCHETYPES.md (original, owned content —
// authored fresh, NOT derived from MBTI / 16Personalities / NERIS text). Each
// archetype maps to one Big Five trait pole. This is the role-detail content
// source for PersonalityResults (a TS const, no DB table).
//
// ⚠️ IP guardrails (spec §2): no MBTI/Myers-Briggs/16Personalities/NERIS terms,
// no E/I/S/N/T/F/J/P axes, no 4-letter type codes, no NERIS type nicknames.

/** Fixed UUID of the new (v2) Personality assessment. The rich UI/AI branch on
 *  THIS id — not on type='personality' alone — since the retired Profile (…002)
 *  shares the type. */
export const PERSONALITY_V2_ID = 'a1000000-0000-0000-0000-000000000012';

export type PersonalityTrait =
  | 'Openness'
  | 'Conscientiousness'
  | 'Extraversion'
  | 'Agreeableness'
  | 'Emotional Stability';

export type TraitPole = 'high' | 'low';

export interface PersonalityTraitMeta {
  key: PersonalityTrait;
  /** Low-end spectrum label. */
  lowLabel: string;
  /** High-end spectrum label. */
  highLabel: string;
  /** One-line description of what the trait captures. */
  blurb: string;
}

/** The 5 traits in canonical order, with their spectrum labels (spec §3). */
export const PERSONALITY_TRAITS: PersonalityTraitMeta[] = [
  { key: 'Openness', lowLabel: 'Practical · grounded', highLabel: 'Curious · imaginative', blurb: 'How drawn you are to ideas, novelty, and possibility.' },
  { key: 'Conscientiousness', lowLabel: 'Flexible · spontaneous', highLabel: 'Organized · driven', blurb: 'How much you plan, organize, and follow through.' },
  { key: 'Extraversion', lowLabel: 'Reflective · reserved', highLabel: 'Outgoing · energized by people', blurb: 'Where you draw energy — from people or from solitude.' },
  { key: 'Agreeableness', lowLabel: 'Candid · analytical', highLabel: 'Warm · cooperative', blurb: 'How much you prioritize harmony, empathy, and cooperation.' },
  { key: 'Emotional Stability', lowLabel: 'Sensitive · responsive', highLabel: 'Calm · resilient', blurb: 'How steady you stay under pressure and setbacks.' },
];

/** Accent color per trait (spectrum bars, archetype detail). */
export const PERSONALITY_TRAIT_COLOR: Record<PersonalityTrait, string> = {
  Openness: '#a855f7',
  Conscientiousness: '#0ea5e9',
  Extraversion: '#f59e0b',
  Agreeableness: '#10b981',
  'Emotional Stability': '#6366f1',
};

export interface PersonalityArchetype {
  name: string;
  trait: PersonalityTrait;
  pole: TraitPole;
  /** e.g. "Openness — High". */
  traitSignature: string;
  essence: string;
  strengths: string[];
  growthEdges: string[];
  atWork: string;
  inRelationships: string;
  /** "Pairs Well With" sentence. */
  pairsWith: string;
}

/** The 10 archetypes (one per trait pole), in trait order (high then low). */
export const PERSONALITY_ARCHETYPES: PersonalityArchetype[] = [
  {
    name: 'The Voyager',
    trait: 'Openness',
    pole: 'high',
    traitSignature: 'Openness — High',
    essence:
      'The Voyager is pulled toward the new and the possible. You live a few steps ahead of the present, drawn to ideas, patterns, and "what if" — and you\'d rather discover than repeat.',
    strengths: [
      'Sees connections and possibilities others miss.',
      'Learns fast and follows curiosity into unfamiliar territory.',
      'Brings fresh framing that unsticks stale problems.',
      'Comfortable with ambiguity and change.',
    ],
    growthEdges: [
      'Can chase novelty and leave things half-finished.',
      'May overlook practical constraints or proven solutions.',
      'Risks scattering energy across too many interests.',
      'Benefits from a finisher to ground the ideas.',
    ],
    atWork:
      'Thrives in early-stage, open-ended, creative work — strategy, design, research, anything needing a new angle. Less energized by repetition and rigid process.',
    inRelationships:
      'Brings wonder, conversation, and a willingness to grow. Wants a partner who explores with them; can read routine as stagnation.',
    pairsWith: 'The Steward (turns ideas into plans) · The Pragmatist (keeps them grounded).',
  },
  {
    name: 'The Pragmatist',
    trait: 'Openness',
    pole: 'low',
    traitSignature: 'Openness — Low',
    essence:
      'The Pragmatist trusts what works. You\'re grounded, concrete, and practical — more interested in solving the real problem in front of you than in theory or novelty for its own sake.',
    strengths: [
      'Realistic, sensible judgment; hard to distract with hype.',
      'Relies on proven methods and tangible evidence.',
      'Steady, consistent, and dependable in execution.',
      'Cuts through abstraction to the actual issue.',
    ],
    growthEdges: [
      'May dismiss new ideas before testing them.',
      'Can default to "the way it\'s always been done."',
      'Risks missing the bigger pattern or future shift.',
      'Benefits from deliberately inviting fresh input.',
    ],
    atWork:
      'Excellent at operations, delivery, and anything that rewards reliability and common sense. Anchors teams that might otherwise drift into endless ideation.',
    inRelationships:
      'Dependable, low-drama, present. Shows care through practical support; may undervalue novelty or big-picture dreaming a partner craves.',
    pairsWith: 'The Voyager (expands their horizon) · The Catalyst (adds spark).',
  },
  {
    name: 'The Steward',
    trait: 'Conscientiousness',
    pole: 'high',
    traitSignature: 'Conscientiousness — High',
    essence:
      'The Steward turns intention into follow-through. You\'re organized, dependable, and driven — you make plans and you keep them, and people trust that what you take on gets done.',
    strengths: [
      'Disciplined, reliable, and detail-aware.',
      'Strong planning, structure, and goal pursuit.',
      'Finishes what they start; honors commitments.',
      'Brings order to complexity.',
    ],
    growthEdges: [
      'Can be rigid or perfectionistic.',
      'May struggle to adapt when the plan breaks.',
      'Risks overwork and difficulty delegating.',
      'Benefits from practicing flexibility and rest.',
    ],
    atWork:
      'A backbone for execution, project delivery, and quality. Excels where standards and consistency matter; can chafe in chaotic, undefined environments.',
    inRelationships:
      'Loyal, steady, follows through on the small things. May read a partner\'s spontaneity as carelessness; grows by loosening the grip on control.',
    pairsWith: 'The Improviser (loosens them up) · The Voyager (supplies the vision).',
  },
  {
    name: 'The Improviser',
    trait: 'Conscientiousness',
    pole: 'low',
    traitSignature: 'Conscientiousness — Low',
    essence:
      'The Improviser thrives unscripted. You\'re spontaneous, adaptable, and relaxed about structure — at your best when you can respond to the moment rather than follow a rigid plan.',
    strengths: [
      'Flexible and quick to adapt when things change.',
      'Comfortable improvising under shifting conditions.',
      'Easygoing; doesn\'t sweat the small stuff.',
      'Keeps options open and momentum loose.',
    ],
    growthEdges: [
      'Can leave tasks unfinished or run late.',
      'May resist structure that would actually help.',
      'Risks unreliability on long commitments.',
      'Benefits from lightweight systems and deadlines.',
    ],
    atWork:
      'Shines in fast-changing, reactive roles — crisis response, fluid creative work, environments that reward agility over process. Struggles with heavy admin and rigid timelines.',
    inRelationships:
      'Fun, flexible, low-pressure company. Can frustrate a structured partner with follow-through; grows by honoring the commitments that matter to others.',
    pairsWith: 'The Steward (adds structure) · The Anchor (adds steadiness).',
  },
  {
    name: 'The Catalyst',
    trait: 'Extraversion',
    pole: 'high',
    traitSignature: 'Extraversion — High',
    essence:
      'The Catalyst brings energy to people. You\'re outgoing, expressive, and recharged by connection — you spark conversation, rally a group, and think out loud with others.',
    strengths: [
      'Energizes rooms and builds momentum.',
      'Connects easily and initiates with new people.',
      'Communicates and persuades with warmth.',
      'Comfortable being seen and leading out front.',
    ],
    growthEdges: [
      'Can dominate the airtime or talk over quieter voices.',
      'May seek stimulation and struggle with solitude.',
      'Risks acting before reflecting.',
      'Benefits from making space for others and for stillness.',
    ],
    atWork:
      'Natural in roles built on people — leading, selling, facilitating, rallying. Less at home in long solo, heads-down work.',
    inRelationships:
      'Expressive, engaged, socially generous. Needs interaction to feel alive; grows by tuning into a partner who recharges quietly.',
    pairsWith: 'The Contemplative (adds depth) · The Harmonizer (channels the energy).',
  },
  {
    name: 'The Contemplative',
    trait: 'Extraversion',
    pole: 'low',
    traitSignature: 'Extraversion — Low',
    essence:
      'The Contemplative draws strength from the inner world. You\'re reflective and reserved, recharging in solitude and thinking deeply before you speak.',
    strengths: [
      'Reflective, focused, and self-aware.',
      'Comfortable with deep solo work and concentration.',
      'Listens well; speaks with intention.',
      'Brings calm, considered presence.',
    ],
    growthEdges: [
      'Can withdraw or under-share when it matters.',
      'May be overlooked in loud, fast group settings.',
      'Risks isolation when stressed.',
      'Benefits from voicing thoughts before they\'re "perfect."',
    ],
    atWork:
      'Excels at deep, independent, detail-rich work — writing, analysis, craft, research. Drained by constant meetings and high-stimulation environments.',
    inRelationships:
      'Loyal, thoughtful, present in a quiet way. Needs alone time to recharge; grows by reaching out and letting others in rather than retreating.',
    pairsWith: 'The Catalyst (draws them out) · The Anchor (shares the calm).',
  },
  {
    name: 'The Harmonizer',
    trait: 'Agreeableness',
    pole: 'high',
    traitSignature: 'Agreeableness — High',
    essence:
      'The Harmonizer holds the group together. You\'re warm, empathic, and cooperative — attuned to how people feel and quick to smooth friction and build trust.',
    strengths: [
      'Reads emotions and builds cohesion.',
      'Generous, supportive, and trustworthy.',
      'De-escalates conflict and finds common ground.',
      'Makes others feel seen and safe.',
    ],
    growthEdges: [
      'Can avoid necessary conflict or hard truths.',
      'May over-give and neglect their own needs.',
      'Risks being taken advantage of.',
      'Benefits from healthy boundaries and candor.',
    ],
    atWork:
      'Vital to teamwork, care, mediation, and culture. Keeps groups aligned and humane; can struggle with blunt decisions or holding people accountable.',
    inRelationships:
      'Devoted, attentive, deeply caring. Puts others first — sometimes too far; grows by asking for what they need and saying the hard thing kindly.',
    pairsWith: 'The Challenger (adds candor) · The Catalyst (shares warmth outward).',
  },
  {
    name: 'The Challenger',
    trait: 'Agreeableness',
    pole: 'low',
    traitSignature: 'Agreeableness — Low',
    essence:
      'The Challenger says the hard thing. You\'re candid, analytical, and objective — you\'ll question the consensus, give blunt feedback, and hold the line on what\'s true over what\'s comfortable.',
    strengths: [
      'Honest and direct; won\'t sugarcoat.',
      'Decides with logic, undistracted by approval.',
      'Willing to stand alone and push back.',
      'Drives high standards and tough calls.',
    ],
    growthEdges: [
      'Can come across as harsh or combative.',
      'May undervalue others\' feelings and morale.',
      'Risks unnecessary friction in close ties.',
      'Benefits from pairing candor with warmth.',
    ],
    atWork:
      'Valuable where rigor and hard decisions matter — strategy, critique, negotiation, quality gates. Can strain team morale if candor outruns tact.',
    inRelationships:
      'Straight with you, fiercely loyal underneath the edge. Can wound with bluntness; grows by leading with empathy before the critique.',
    pairsWith: 'The Harmonizer (softens the edge) · The Steward (channels the rigor).',
  },
  {
    name: 'The Anchor',
    trait: 'Emotional Stability',
    pole: 'high',
    traitSignature: 'Emotional Stability — High',
    essence:
      'The Anchor stays steady when things shake. You\'re calm, resilient, and even-keeled — pressure and setbacks move you less, and others borrow your composure.',
    strengths: [
      'Calm and grounded under stress.',
      'Recovers quickly from setbacks.',
      'Takes criticism without spiraling.',
      'A stabilizing presence for others.',
    ],
    growthEdges: [
      'Can seem detached or under-react to real problems.',
      'May miss emotional cues in self or others.',
      'Risks complacency when urgency is warranted.',
      'Benefits from tuning into feeling, not just staying level.',
    ],
    atWork:
      'Built for high-pressure, high-stakes, crisis-prone roles. Holds the center when others panic; can underestimate emotional undercurrents on a team.',
    inRelationships:
      'Reassuring, stable, hard to rattle. The steady one in a storm; grows by engaging emotionally rather than staying coolly above it.',
    pairsWith: 'The Attuned (adds emotional depth) · The Improviser (steadies the chaos).',
  },
  {
    name: 'The Attuned',
    trait: 'Emotional Stability',
    pole: 'low',
    traitSignature: 'Emotional Stability — Low',
    essence:
      'The Attuned feels deeply and responds fully. You\'re sensitive and emotionally responsive — you register stress and feeling vividly, which makes you perceptive, passionate, and alert to what matters.',
    strengths: [
      'Highly attuned to emotion in self and others.',
      'Passionate, empathic, and expressive.',
      'Alert to risk and to what needs care.',
      'Brings depth and authenticity of feeling.',
    ],
    growthEdges: [
      'Can be thrown off by stress and worry.',
      'May take criticism or small problems personally.',
      'Risks overwhelm and rumination.',
      'Benefits from grounding habits and self-compassion.',
    ],
    atWork:
      'Strong in roles needing empathy, vigilance, and emotional nuance — care, creative, relational work. Needs a supportive, lower-chaos environment to do their best.',
    inRelationships:
      'Warm, deeply feeling, emotionally present. Loves intensely and feels keenly; grows with steadying routines and a partner who offers reassurance.',
    pairsWith: 'The Anchor (offers steadiness) · The Harmonizer (shares the warmth).',
  },
];

/** Lookup an archetype by trait + pole (every trait pole has exactly one). */
export function archetypeFor(trait: PersonalityTrait, pole: TraitPole): PersonalityArchetype {
  return (
    PERSONALITY_ARCHETYPES.find(a => a.trait === trait && a.pole === pole) ??
    PERSONALITY_ARCHETYPES[0]
  );
}

/** Lookup an archetype by its display name. */
export const personalityArchetypeByName: Record<string, PersonalityArchetype> = Object.fromEntries(
  PERSONALITY_ARCHETYPES.map(a => [a.name, a]),
);
